import { useCallback, useContext, useRef } from 'react';

import { Drop, Button, BottomSheet, useBreakpoint, TooltipHover } from '@hh.ru/magritte-ui';
import { ChevronDownOutlinedSize16 } from '@hh.ru/magritte-ui/icon';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import ChangeTopicStageItem from 'src/components/ChangeTopicDropStageItem';
import { ResumeCardActionsContext } from 'src/components/ResumeCard/Actions/Context';
import translation from 'src/components/translation';
import { useSelector } from 'src/hooks/useSelector';
import useToggleState from 'src/hooks/useToggleState';
import { NegotiationLink, TransitionType } from 'src/models/employerNegotiations/negotiationLinks';
import { StateId } from 'src/models/employerNegotiations/stateId.types';

import styles from './styles.less';

const TrlKeys = {
    [TransitionType.Discard]: 'vacancyResponse.discard',
    [TransitionType.ChangeTopic]: 'employer.candidates.buttons.changeTopic',
    [TransitionType.Invite]: 'vacancyResponse.invite',
    hiringManagerTooltip: 'employer.responses.hiringManager.tooltip',
};

interface ButtonWithSubstates {
    type: TransitionType.Invite | TransitionType.ChangeTopic | TransitionType.Discard;
    links: NegotiationLink[];
    sendAnalytics: (state: StateId) => void;
    stretched: boolean;
}

const BUTTON_DATA_QA_BY_TYPE = {
    [TransitionType.Invite]: 'employee-invite-on-topic',
    [TransitionType.ChangeTopic]: 'employee-change-topic',
    [TransitionType.Discard]: 'employee-discard-on-topic',
};

const ButtonWithSubstates: TranslatedComponent<ButtonWithSubstates> = ({
    type,
    links,
    stretched,
    sendAnalytics,
    trls,
}) => {
    const isHiringManager = useSelector((state) => state.isHiringManager);
    const { isM } = useBreakpoint();
    const menuActivatorRef = useRef(null);
    const [isMenuVisible, toggleMenuVisible, setMenuVisible] = useToggleState(false);
    const handleMenuClose = useCallback(() => setMenuVisible(false), [setMenuVisible]);
    const {
        resume: { topicId },
    } = useContext(ResumeCardActionsContext);
    const topicData = useSelector((state) =>
        topicId ? state.candidatesList?.shortTopicList?.topicList[topicId] : undefined
    );
    const lastFunnelStage = topicData?.lastFunnelStage;

    const buttonText = trls[TrlKeys[type]];
    const buttonQa = BUTTON_DATA_QA_BY_TYPE[type];

    const content = links.map(({ id, state }) => (
        <ChangeTopicStageItem
            key={id}
            stateName={state}
            topicId={topicId}
            lastFunnelStage={lastFunnelStage}
            sendAnalytics={sendAnalytics}
        />
    ));

    return (
        <>
            <Drop
                visible={isMenuVisible}
                onClose={handleMenuClose}
                activatorRef={menuActivatorRef}
                role="status"
                placement="bottom-left"
                space={0}
            >
                <div className={styles.drop}>{content}</div>
            </Drop>
            <BottomSheet visible={isMenuVisible} onClose={handleMenuClose}>
                {content}
            </BottomSheet>
            <Button
                ref={menuActivatorRef}
                stretched={stretched}
                mode={'secondary'}
                size={'small'}
                onClick={toggleMenuVisible}
                data-qa={buttonQa}
                {
                    // Скрываем шевроны на M экранах
                    ...(!isM
                        ? {
                              icon: <ChevronDownOutlinedSize16 />,
                              'aria-label': buttonText,
                          }
                        : {
                              icon: undefined,
                          })
                }
            >
                {buttonText}
            </Button>
            {isHiringManager && type === TransitionType.Invite && (
                <TooltipHover activatorRef={menuActivatorRef} placement="top-center">
                    {trls[TrlKeys.hiringManagerTooltip]}
                </TooltipHover>
            )}
        </>
    );
};

export default translation(ButtonWithSubstates);
